// src/pages/SuperAdminUsers_List.jsx
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import SuperAdminNav from "../components/superAdminNav";
import Time from "../components/time";
import { Link } from "react-router-dom";
import {
  Users,
  Search,
  Plus,
  RefreshCw,
  ArrowUp,
  ArrowDown,
  ChevronsUpDown,
  PencilLine,
  Trash2,
  Loader2,
} from "lucide-react";

const SORTABLE = ["username","email","display_name","role","is_active","last_login_at","created_at"];
const fmtDate = (s) => (s ? new Date(s).toLocaleString() : "");

export default function SuperAdminUsers_List() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [active, setActive] = useState(""); // '', '1', '0'
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [sortBy, setSortBy] = useState("created_at");
  const [sortDir, setSortDir] = useState("desc");
  const [loading, setLoading] = useState(false);

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(total / perPage)),
    [total, perPage]
  );

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get("https://superadmin.nissiindia.com/api/superadmin/users", {
        params: { q, active, page, perPage, sortBy, sortDir },
      });
      setRows(r.data?.rows || []);
      setTotal(r.data?.total || 0);
    } catch (e) {
      console.error(e);
      Swal.fire("Error", "Failed to load users", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // eslint-disable-next-line
    load();
  }, [q, active, page, perPage, sortBy, sortDir]);

  const toggleSort = (col) => {
    if (!SORTABLE.includes(col)) return;
    if (sortBy === col) setSortDir(sortDir === "asc" ? "desc" : "asc");
    else {
      setSortBy(col);
      setSortDir("asc");
    }
  };

  const delUser = async (u) => {
    const res = await Swal.fire({
      icon: "warning",
      title: `Delete ${u.username}?`,
      text: "This action cannot be undone.",
      showCancelButton: true,
      confirmButtonText: "Yes, delete",
      cancelButtonText: "Cancel",
    });
    if (!res.isConfirmed) return;
    try {
      await axios.delete(`https://superadmin.nissiindia.com/api/superadmin/users/${u.user_id}`);
      Swal.fire({ icon: "success", title: "Deleted" });
      if (rows.length === 1 && page > 1) setPage(page - 1);
      else load();
    } catch (e) {
      console.error(e);
      Swal.fire("Error", e.response?.data?.error || "Delete failed", "error");
    }
  };

  const activeBadge = (is) =>
    is
      ? "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200"
      : "bg-rose-50 text-rose-700 ring-1 ring-rose-200";

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Responsive: drawer sidebar on mobile, 25%/75% on md+ */}
      <div className="flex flex-col md:flex-row md:h-screen md:overflow-hidden">
        <SuperAdminNav />

        {/* Main content */}
        <main className="w-full md:w-3/4 md:h-full md:overflow-auto py-6 px-4 sm:px-6">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-slate-900 text-white">
                <Users className="h-5 w-5" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-slate-900">Super Admin Users</h2>
                <p className="text-sm text-slate-500">Manage global super admin accounts</p>
              </div>
            </div>
            <div className="text-sm text-slate-500">
              <Time />
            </div>
          </div>

          {/* Top controls */}
          <div className="mt-5 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
            <div className="flex items-center gap-2">
              <Link to="/superAdmin/superAdminUsers_Add">
                <button className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-white shadow hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500/30">
                  <Plus className="h-4 w-4" />
                  Add New
                </button>
              </Link>
              <button
                onClick={load}
                disabled={loading}
                className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-2 text-slate-900 shadow-sm hover:bg-slate-50 disabled:opacity-50"
              >
                <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
                Refresh
              </button>
              <span className="text-slate-600 ml-2">Total: {total}</span>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
              <div className="relative w-full sm:w-80">
                <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <input
                  placeholder="Search username / email / name"
                  value={q}
                  onChange={(e) => {
                    setPage(1);
                    setQ(e.target.value);
                  }}
                  className="w-full rounded-lg border border-slate-300 bg-white pl-9 pr-3 py-2 text-slate-900 placeholder:text-slate-400 shadow-sm focus:border-slate-400 focus:ring-2 focus:ring-blue-500/20"
                />
              </div>

            
             
            </div>
          </div>

          {/* Table */}
          <div className="mt-6 overflow-x-auto rounded-xl border border-slate-200 bg-white shadow">
            <table className="min-w-[1000px] w-full text-sm">
              <thead className="bg-slate-900 text-slate-100">
  <tr className="text-left">
    <th className="px-4 py-3 font-semibold border-b border-slate-800/70">Username</th>
    <th className="px-4 py-3 font-semibold border-b border-slate-800/70">Display Name</th>
    <th className="px-4 py-3 font-semibold border-b border-slate-800/70">Email</th>
    <th className="px-4 py-3 font-semibold border-b border-slate-800/70">Role</th>
    <th className="px-4 py-3 font-semibold border-b border-slate-800/70">Active</th>
    <th className="px-4 py-3 font-semibold border-b border-slate-800/70">Last Login</th>
    <th className="px-4 py-3 font-semibold border-b border-slate-800/70">Created</th>
    <th className="px-4 py-3 font-semibold border-b border-slate-800/70 w-24">Edit</th>
    <th className="px-4 py-3 font-semibold border-b border-slate-800/70 w-28">Delete</th>
  </tr>
</thead>


              <tbody className="divide-y divide-slate-100">
                {loading ? (
                  <tr>
                    <td colSpan={8} className="px-4 py-10 text-center">
                      <span className="inline-flex items-center gap-2 text-slate-500">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Loading...
                      </span>
                    </td>
                  </tr>
                ) : rows.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-4 py-10 text-center text-slate-500">
                      No records
                    </td>
                  </tr>
                ) : (
                  rows.map((u) => (
                    <tr
                      key={u.user_id}
                      className="odd:bg-white even:bg-slate-50 hover:bg-slate-100/60"
                    >
                      <td className="px-4 py-3 text-slate-900">{u.username}</td>
                      <td className="px-4 py-3 text-slate-900">{u.display_name || ""}</td>
                      <td className="px-4 py-3 text-slate-900">{u.email || ""}</td>
                      <td className="px-4 py-3 text-slate-700">{u.role}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium ${activeBadge(u.is_active)}`}>
                          {u.is_active ? "Yes" : "No"}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-slate-700">{fmtDate(u.last_login_at)}</td>
                      <td className="px-4 py-3 text-slate-700">{fmtDate(u.created_at)}</td>
                      <td className="px-4 py-3">
                        
                          <Link to={`/superAdmin/superAdminUsers_Edit/${u.user_id}`}>
                            <button
                              className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-slate-900 shadow-sm hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                              title="Edit"
                            >
                              <PencilLine className="h-4 w-4" />
                            </button>
                          </Link>
                          
                            </td>
                            <td>
                          <button
                            onClick={() => delUser(u)}
                            className="inline-flex items-center gap-2 rounded-lg bg-rose-600 px-3 py-1.5 text-white shadow hover:bg-rose-700 focus:outline-none focus:ring-2 focus:ring-rose-500/30"
                            title="Delete"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="mt-4 flex items-center justify-center gap-2">
            <button
              disabled={page <= 1}
              onClick={() => setPage(1)}
              className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-slate-900 shadow-sm hover:bg-slate-50 disabled:opacity-50"
              title="First"
            >
              &laquo;
            </button>
            <button
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-slate-900 shadow-sm hover:bg-slate-50 disabled:opacity-50"
              title="Previous"
            >
              Prev
            </button>
            <span className="text-slate-600">Page {page} / {totalPages}</span>
            <button
              disabled={page >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-slate-900 shadow-sm hover:bg-slate-50 disabled:opacity-50"
              title="Next"
            >
              Next
            </button>
            <button
              disabled={page >= totalPages}
              onClick={() => setPage(totalPages)}
              className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-slate-900 shadow-sm hover:bg-slate-50 disabled:opacity-50"
              title="Last"
            >
              &raquo;
            </button>
          </div>
        </main>
      </div>
    </div>
  );
}

function Th({ label, col, sortBy, sortDir, onClick }) {
  const is = sortBy === col;
  return (
    <th className="px-4 py-3 font-semibold border-b border-slate-200">
      <button
        onClick={() => onClick(col)}
        className="inline-flex items-center gap-1 text-slate-700 hover:text-slate-900 select-none"
        title="Click to sort"
      >
        <span>{label}</span>
        {is ? (
          sortDir === "asc" ? (
            <ArrowUp className="h-3.5 w-3.5" />
          ) : (
            <ArrowDown className="h-3.5 w-3.5" />
          )
        ) : (
          <ChevronsUpDown className="h-3.5 w-3.5 text-slate-400" />
        )}
      </button>
    </th>
  );
}
